import React, { useState, useEffect } from 'react';
import {
  ArrowRight, PlusCircle, MinusCircle, Download,
  Landmark, Wallet, Percent, FileText, ChevronRight,
  PiggyBank, BriefcaseBusiness,
  BadgeDollarSign, Upload, DownloadCloud,
  HandCoins, Filter, ChevronLeft,
  Database,
  PieChart,
  Calendar,
  CirclePercent, X
} from 'lucide-react';

import styles from './LoanLedger.module.css';
import Sidebar2 from '../../Components/SideBar2/SideBar2';
import Header2 from '../../Components/Header2/Header2';
import axios from 'axios';
import { BASE_URL } from '../../Components/Api/Api';
import { Form, Placeholder, OverlayTrigger, Tooltip, Modal, Button } from "react-bootstrap";
import Select from "react-select";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { AiFillPrinter } from "react-icons/ai";
import { FaFilter } from "react-icons/fa";

const tableColumnCount = 8; // S/N, Reference No, Description, Transaction Date, Type, Debit, Credit, Balance

export default function LoanLedger() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [loading, setLoading] = useState(true);       // table loading
  const [statsLoading, setStatsLoading] = useState(true); // cards loading

  const [ledgers, setLedgers] = useState([]);          // full dataset for the currently selected loan
  const [displayedLedgers, setDisplayedLedgers] = useState([]); // dataset after the date filter is applied
  const [stats, setStats] = useState({});

  const [savings, setSavings] = useState([]);
  const [savingsType, setSavingsType] = useState(null);

  // ---- filter modal state ----
  const [showFilterModal, setShowFilterModal] = useState(false);

  // ---- date range filter state ----
  // Filtering only happens once BOTH a start and an end date are set.
  // Until then, the table keeps showing whatever it was showing before
  // (i.e. it does not filter on a partial/incomplete range).
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const isDateFilterActive = Boolean(startDate || endDate);

  const [tempSavingsType, setTempSavingsType] = useState("");
const [tempStartDate, setTempStartDate] = useState("");
const [tempEndDate, setTempEndDate] = useState("");

  // Any filter (loan type and/or date range) currently applied
  const isFilterActive = Boolean(savingsType) || isDateFilterActive;

const CardSkeleton = () => (
  <div className={styles.statCard}>
    {/* Icon */}
    <Placeholder
      as="div"
      animation="glow"
      style={{
        width: 36,
        height: 36,
        borderRadius: 8,
        display: "flex",
      }}
    >
      <Placeholder
        xs={12}
        style={{
          width: "100%",
          height: "100%",
          borderRadius: 8,
        }}
      />
    </Placeholder>

    <div className={styles.statContent} style={{ width: "100%" }}>
      <Placeholder animation="glow">
        <Placeholder
          xs={5}
          style={{
            display: "block",
            height: 12,
            marginBottom: 8,
            borderRadius: 4,
          }}
        />

        <Placeholder
          xs={7}
          style={{
            display: "block",
            height: 22,
            marginBottom: 18,
            borderRadius: 4,
          }}
        />

        <Placeholder
          xs={4}
          style={{
            display: "block",
            height: 10,
            marginTop: 10,
            borderRadius: 4,
          }}
        />
      </Placeholder>
    </div>
  </div>
);

const formatCurrency = (value) => `₦${Number(value || 0).toLocaleString('en-NG')}`;
  const formatCurrencys = (value) =>
    Number(value ?? 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });


const formatMetricValue = (value, type = "number") => {
  const numericValue = Number(value);

  // Handle invalid values
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    Number.isNaN(numericValue)
  ) {
    return type === "currency" ? "₦0.00" : "0";
  }

  // Percent
  if (type === "percent") {
    return `${numericValue.toFixed(0)}%`;
  }

  // Currency
  if (type === "currency") {
    const absValue = Math.abs(numericValue);
    const sign = numericValue < 0 ? "-" : "";

    let formatted;

    if (absValue >= 1_000_000_000_000) {
      formatted = `${(absValue / 1_000_000_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}T`;
    } else if (absValue >= 1_000_000_000) {
      formatted = `${(absValue / 1_000_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}B`;
    } else if (absValue >= 1_000_000) {
      formatted = `${(absValue / 1_000_000)
        .toFixed(1)
        .replace(/\.0$/, "")}M`;
    } else if (absValue >= 1_000) {
      formatted = `${(absValue / 1_000)
        .toFixed(1)
        .replace(/\.0$/, "")}K`;
    } else {
      formatted = absValue.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }

    return `${sign}₦${formatted}`;
  }

  // Normal numbers
  if (Number.isInteger(numericValue)) {
    return numericValue.toLocaleString("en-NG");
  }

  return numericValue.toLocaleString("en-NG", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

  const filterLedgersByRange = (list, start, end) => {
  return list.filter((item) => {
    const txDate = item.transaction_date;

    if (!txDate) return false;

    // Both selected
    if (start && end) {
      return txDate >= start && txDate <= end;
    }

    // Only start selected
    if (start) {
      return txDate >= start;
    }

    // Only end selected
    if (end) {
      return txDate <= end;
    }

    // No filter
    return true;
  });
};

  // ---- data fetching ----
  // dateRange (optional) lets callers force an explicit start/end instead of
  // relying on the startDate/endDate state closure - this matters when we
  // clear filters, since state updates from the same handler aren't visible
  // yet inside this function on that call.
  const fetchLedger = async (loanId = null, dateRange = null) => {
    setLoading(true);
    setStatsLoading(true);

    try {
      const token = localStorage.getItem("token");
      const companyid = localStorage.getItem("companyId");
      const memberid = localStorage.getItem("memberId");

      let url = `${BASE_URL}/my_loan_ledger?company_id=${companyid}&member_id=${memberid}`;
      if (loanId) {
        url += `&loan_id=${loanId}`;
      }

      const res = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const data = res.data?.data || {};
      const ledgerData = data.ledger || [];

      setLedgers(ledgerData);
      setStats(data.summary || {});
      setCurrentPage(1);

      const effectiveStart = dateRange ? dateRange.start : startDate;
      const effectiveEnd = dateRange ? dateRange.end : endDate;

      // Keep whatever date range filter is currently active applied to the freshly fetched data
      // if (effectiveStart && effectiveEnd) {
      //   setDisplayedLedgers(filterLedgersByRange(ledgerData, effectiveStart, effectiveEnd));
      // } else {
      //   setDisplayedLedgers(ledgerData);
      // }

      setDisplayedLedgers(
      filterLedgersByRange(
        ledgerData,
        effectiveStart,
        effectiveEnd
      )
    );
    } catch (error) {
      console.error(error);
      setLedgers([]);
      setDisplayedLedgers([]);
      setStats({});
    } finally {
      setLoading(false);
      setStatsLoading(false);
    }
  };

  const fetchInvoices = async () => {
    try {
      const token = localStorage.getItem("token");
      const companyid = localStorage.getItem("companyId");
      const memberid = localStorage.getItem("memberId");

      const res = await axios.get(
        `${BASE_URL}/member-auth/member_loan_plans?company_id=${companyid}&member_id=${memberid}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/json",
          },
        }
      );

      const savingsData = res.data?.data || [];
      setSavings(savingsData);
    } catch (error) {
      console.error(error);
      setSavings([]);
    }
  };

  useEffect(() => {
    fetchLedger();      // default load — no loan_id
    fetchInvoices();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savingsOptions = (Array.isArray(savings) ? savings : [])
  .filter((item) => item && typeof item === "object")
  .map((item) => ({
    value: item.loan?.id,
    label: item.loan?.loan_name || item.type?.product_name,
    ...item.type,
  }));

  const selectStyles = {
    control: (base) => ({
      ...base,
      borderRadius: "10px",
      fontSize: "12px",
      minHeight: 40,
      height: "auto",
    }),
    valueContainer: (base) => ({
      ...base,
      height: 40,
      padding: "0 8px",
    }),
    input: (base) => ({
      ...base,
      margin: 0,
      padding: 0,
    }),
    indicatorsContainer: (base) => ({
      ...base,
      height: 40,
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#15803d"
        : state.isFocused
        ? "#EBFFF3"
        : "transparent",
      color: state.isSelected ? "#fff" : "inherit",
      fontSize: "12px",
      cursor: "pointer",
    }),
    placeholder: (base) => ({
      ...base,
      color: "#6c757d",
      fontSize: "12px",
    }),
    singleValue: (base) => ({
      ...base,
      fontSize: "12px",
      color: "#212529",
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "0.375rem",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      zIndex: 9999,
    }),
  };

  // ---- filter handlers ----
  const handleSavingsTypeChange = (selected) => {
    setSavingsType(selected);
    fetchLedger(selected?.value || null); // now sent as loan_id
  };


  const showDateWarning = () => {
  Swal.fire({
    icon: "warning",
    title: "Incomplete Date Range",
    text: "Please select both Start Date and End Date.",
    confirmButtonColor: "#15803d",
    heightAuto: false,
  });
};

  // Only applies the range filter once both start and end dates are present.
  // Picking just a start date (or just an end date) does not filter yet -
  // it waits for the second date to complete the range.
const handleStartDateChange = (e) => {
  const newStart = e.target.value;

  setStartDate(newStart);
  setCurrentPage(1);

  setDisplayedLedgers(
    filterLedgersByRange(ledgers, newStart, endDate)
  );

  // setShowFilterModal(false);
};

const handleEndDateChange = (e) => {
  const newEnd = e.target.value;

  setEndDate(newEnd);
  setCurrentPage(1);

  setDisplayedLedgers(
    filterLedgersByRange(ledgers, startDate, newEnd)
  );

  // setShowFilterModal(false);
};


  // const handleClearAllFilters = () => {
  //   setSavingsType(null);
  //   setStartDate("");
  //   setEndDate("");
  //   setCurrentPage(1);
  //   fetchLedger(null, { start: "", end: "" });
  // };

  const handleClearAllFilters = () => {
  setSavingsType("");
  setTempSavingsType("");

  setStartDate("");
  setEndDate("");

  setTempStartDate("");
  setTempEndDate("");

  setCurrentPage(1);

  fetchLedger(null, {
    start: "",
    end: "",
  });

  setShowFilterModal(false);
};

 const formatMoney = (value) => {
    const num = Number(value || 0);
    return num.toLocaleString("en-NG", { minimumFractionDigits: 2 });
  };

  // let currentRunningTotal = 0;
  // const dataWithRunningBalance = displayedLedgers.map((row) => {
  //   const debitAmount = Number(row.debit || 0);
  //   const creditAmount = Number(row.credit || 0);
  //   // const balances = Number(row.balance || 0);

  //   // Calculate: Previous Balance + current Debit - current Credit
  //   currentRunningTotal = currentRunningTotal + creditAmount - debitAmount;

  //   return {
  //     ...row,
  //     // Store numeric value for the Closing Balance calculation
  //     numericRunningBalance: currentRunningTotal,
  //     // Format for display in the table
  //     runningBalance: formatMoney(currentRunningTotal),
  //   };
  // });
  // Get the last item to display the final Closing Balance
  
  let runningLoanBalance = 0;

const loanData = displayedLedgers.map((row) => {
  const debit = Number(row.debit || 0);
  const credit = Number(row.credit || 0);

  runningLoanBalance =
    runningLoanBalance - debit + credit;

  return {
    ...row,
    numericRunningBalance: runningLoanBalance,
    runningBalance: formatMoney(runningLoanBalance),
  };
});

  // const lastRow = dataWithRunningBalance[dataWithRunningBalance.length - 1];
  const lastRow = loanData[loanData.length - 1];

  // ---- pagination (driven by the actual API data) ----
  // const totalItems = displayedLedgers.length;
  // const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  // const startIndex = (currentPage - 1) * itemsPerPage;
  // const endIndex = startIndex + itemsPerPage;
  // const currentTransactions = displayedLedgers.slice(startIndex, endIndex);

    // ---- pagination (driven by the running-balance dataset) ----
  const totalItems = loanData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / itemsPerPage));
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentTransactions = loanData.slice(startIndex, endIndex);

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
  };

  // ---- stat cards, built from live `stats` state ----
  const loanStats = [
    {
      id: 'owed',
      label: 'Total Loan',
      value: formatMetricValue(stats.total_loan_owe, "currency"),
      detail: 'Principal amount owed',
      bgColor: '#eaf8ef',
      textColor: '#059447',
      icon: <Wallet size={22} color="#059447" />,
    },
    {
      id: 'repayment',
      label: 'Total Loan Repayment',
      value: formatMetricValue(stats.total_loan_repayment, "currency"),
      detail: 'All time repayment',
      bgColor: '#dbeafe',
      textColor: '#0284c7',
      icon: <Database size={22} color="#0284c7" />,
    },
    {
      id: 'total_loan_owe',
      label: 'Loan Owed',
      value: formatMetricValue(stats.total_loan_owe, "currency"),
      detail: 'Total Lowed Owed',
      bgColor: '#fee2e2',
      textColor: '#dc2626',
      icon: <PieChart size={22} color="#f59e0b" />,
    },
    {
      id: 'currentMonth',
      label: 'Current Month Repayment',
      value: formatMetricValue(stats.current_month_repayment, "currency"),
      detail: 'Repayment made this month',
      bgColor: '#f3e8ff',
      textColor: '#a855f7',
      icon: <Calendar size={22} color="#9333ea" />,
    },
  ];

     const handleProcess = () => {
      // Check if there is data
      if (!ledgers || ledgers.length === 0) {
          Swal.fire({
              title: "No Data Available",
              text: "Cannot print or generate ledger due to absence of data.",
              icon: "warning",
              confirmButtonText: "OK",
              confirmButtonColor: "#15803d", // Green matching your theme
              heightAuto: false,
          });
          return; // Stop navigation
      }

      // If data exists, proceed with navigation
      navigate("/loans/member-ledger", {
          state: {
              loans: ledgers,
          },
      });
  };

// const handleApplyFilters = async () => {
//   setSavingsType(tempSavingsType);
//   setStartDate(tempStartDate);
//   setEndDate(tempEndDate);
//   setCurrentPage(1);

//   await fetchLedger(
//     tempSavingsType?.value || null,
//     {
//       start: tempStartDate,
//       end: tempEndDate,
//     }
//   );

//   setShowFilterModal(false);
// };

const handleApplyFilters = () => {
  setSavingsType(tempSavingsType);
  setStartDate(tempStartDate);
  setEndDate(tempEndDate);
  setCurrentPage(1);
  setShowFilterModal(false);

  fetchLedger(
    tempSavingsType?.value || null,
    {
      start: tempStartDate,
      end: tempEndDate,
    }
  );
};

// (formatMoney, runningLoanBalance, loanData, lastRow declarations removed from here — now defined above, alongside pagination)


 


  return (
    <div className={styles.container}>
      <Sidebar2 active="loans" />

      <main className={styles.mainContent}>
        <Header2 title="Loan Ledger" />

        <div className={styles.workspace}>
          {/* Stats Cards */}
          <div className={styles.statsGrid}>
            {statsLoading
              ? Array.from({ length: 4 }).map((_, idx) => (
                   <CardSkeleton key={idx} />
                ))
              : loanStats.map((stat) => (
                  <div key={stat.id} className={styles.statCard}>
                    <div className={styles.statIcon} style={{ backgroundColor: stat.bgColor }}>
                      {stat.icon}
                    </div>
                    <div className={styles.statContent}>
                      <p className={styles.statLabel}>{stat.label}</p>
                      <h3 className={styles.statValue} style={{ color: stat.textColor }}>
                        {stat.value}
                      </h3>
                      <p className={styles.statDetail}>{stat.detail}</p>
                    </div>
                  </div>
                ))}
          </div>

          {/* Transaction Ledger Section */}
          <div className={styles.ledgerSection}>
            <div className={styles.ledgerHeader}>
              <div>
                <h2>Loan Transaction Ledger</h2>
                <p>Detailed view of all your loan transactions</p>
              </div>

              <div className={styles.ledgerControls}>
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="open-filter-tooltip">Filter loans</Tooltip>}
                >
                  <button
                    type="button"
                    className={styles.filterBtn}
                    onClick={() => setShowFilterModal(true)}
                  >
                    <FaFilter size={14} style={{ marginRight: 6 }} />
                    Filter
                  </button>
                </OverlayTrigger>

               {isFilterActive && (
                <OverlayTrigger
                  placement="top"
                  overlay={<Tooltip id="clear-all-filters-tooltip">Clear filters</Tooltip>}
                >
                  <button
                    type="button"
                    className={styles.clearFilterBtn}
                    onClick={handleClearAllFilters}
                  >
                    <X size={18} />
                  </button>
                </OverlayTrigger>
              )}

                <button
                  onClick={handleProcess}
                  className={styles.exportBtn}
                  type="button"
                >
                  {/* <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id="print-loan-ledger-tooltip">
                        Print Ledger
                      </Tooltip>
                    }
                  >
                    <AiFillPrinter size={16} />
                  </OverlayTrigger> */}
                   <AiFillPrinter size={16} />  Print Ledger
                </button>
              </div>
            </div>

            {/* Filter Modal */}
            <Modal
              show={showFilterModal}
              onHide={() => setShowFilterModal(false)}
              centered
            >
              <Modal.Header closeButton>
                <Modal.Title style={{ fontSize: "16px" }}>Filter Loans</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: "13px", fontWeight: 600 }}>
                    Loan Type
                  </Form.Label>
                  <Select
                    options={savingsOptions}
                    // value={savingsType}
                    // onChange={handleSavingsTypeChange}
                    value={tempSavingsType}
                    onChange={(selected) => setTempSavingsType(selected)}
                    placeholder="Select Loan Type"
                    isLoading={loading}
                    isClearable
                    styles={selectStyles}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label style={{ fontSize: "13px", fontWeight: 600 }}>
                    Start Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    value={tempStartDate}
                    onChange={(e) => setTempStartDate(e.target.value)}
                    max={endDate || undefined}
                    // value={startDate}
                    // onChange={handleStartDateChange}
                    aria-label="Start date"
                  />
                </Form.Group>

                <Form.Group className="mb-0">
                  <Form.Label style={{ fontSize: "13px", fontWeight: 600 }}>
                    End Date
                  </Form.Label>
                  <Form.Control
                    type="date"
                    // value={endDate}
                    // onChange={handleEndDateChange}
                    value={tempEndDate}
                    onChange={(e) => setTempEndDate(e.target.value)}
                    min={startDate || undefined}
                    aria-label="End date"
                  />
                </Form.Group>
              </Modal.Body>
              <Modal.Footer>
                <button
                  // variant="outline-secondary"
                  // size=""
                  className={styles.cancelbtns}
                onClick={() => setShowFilterModal(false)}
                  // onClick={handleClearAllFilters}
                  // disabled={!isFilterActive}
                >
                  Cancel
                </button>
                <button
                  // size="sm"
                  className={styles.exportBtn}
                  // onClick={() => setShowFilterModal(false)}
                  onClick={handleApplyFilters}
                  disabled={!tempSavingsType && !tempStartDate && !tempEndDate}
                  style={{
                    backgroundColor: "#15803d",
                    borderColor: "#15803d",
                    opacity: (!tempSavingsType && !tempStartDate && !tempEndDate) ? 0.5 : 1,
                    cursor: (!tempSavingsType && !tempStartDate && !tempEndDate) ? "not-allowed" : "pointer",
                  }}
                >
                  Done
                </button>
              </Modal.Footer>
            </Modal>

            {/* Table */}
            <div className={styles.tableWrapper}>
              <table className={styles.transactionTable}>
                <thead>
                  <tr>
                    <th>S/N</th>
                    <th>Reference No.</th>
                    <th>Description</th>
                    <th>Transaction Date</th>
                    <th>Type</th>
                    <th style={{ textAlign: 'right' }}>Debit</th>
                    <th style={{ textAlign: 'right' }}>Credit</th>
                    <th style={{ textAlign: 'right' }}>Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    Array.from({ length: 6 }).map((_, rowIdx) => (
                      <tr key={`skeleton-${rowIdx}`}>
                        {Array.from({ length: tableColumnCount }).map((__, colIdx) => (
                          <td key={colIdx}>
                            <Placeholder as="span" animation="glow">
                              <Placeholder xs={colIdx === 0 ? 4 : 8} />
                            </Placeholder>
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : currentTransactions.length === 0 ? (
                    <tr>
                      <td colSpan={tableColumnCount} style={{ textAlign: 'center', padding: '24px' }}>
                        No transactions found.
                      </td>
                    </tr>
                  ) : (
                    currentTransactions.map((item, index) => {
                      const statusNum = Number(item.status);
                      const isDisbursement = statusNum === 1;
                      const typeLabel = isDisbursement ? 'Disbursement' : statusNum === 2 ? 'Repayment' : '—';
                      const typeColor = isDisbursement ? '#16a34a' : '#dc2626';

                      return (
                        <tr key={item.id}>
                          <td>{startIndex + index + 1}</td>
                          <td>{item.uuid}</td>
                          <td style={{ width: "25%", maxWidth: "25%" }}>{item.description}</td>
                          <td className={styles.dateCell}>{item.transaction_date}</td>
                          <td>
                            <span
                              className={styles.typeTag}
                              style={{
                                color: typeColor,
                                backgroundColor: `${typeColor}15`,
                              }}
                            >
                              {typeLabel}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>{formatCurrencys(item.debit)}</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrencys(item.credit)}</td>
                          <td style={{ textAlign: 'right' }}>{item.runningBalance}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className={styles.paginationContainer}>
              <p className={styles.paginationInfo}>
                {totalItems === 0
                  ? 'Showing 0 of 0 transactions'
                  : `Showing ${startIndex + 1} to ${Math.min(endIndex, totalItems)} of ${totalItems} transactions`}
              </p>

              <div className={styles.paginationControls}>
                <button
                  type="button"
                  className={styles.paginationBtn}
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft size={16} />
                </button>

                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`${styles.paginationBtn} ${
                      page === currentPage ? styles.activePage : ''
                    }`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}

                <button
                  type="button"
                  className={styles.paginationBtn}
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  <ChevronRight size={16} />
                </button>
              </div>

              <div className={styles.itemsPerPage}>
                <select value={itemsPerPage} onChange={handleItemsPerPageChange}>
                  <option value={10}>10 / page</option>
                  <option value={20}>20 / page</option>
                  <option value={50}>50 / page</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}